/**
 * Typed API client for the Legal & Agreements module.
 *
 * Covers MOU / NDA / Partnership / SOW / DPA agreements + their templates,
 * versions and signatures. Mirrors AgreementController.kt on the backend.
 */

import { apiClient } from '@/lib/apiClient';

// ───────────────────────────────────────────────────────────
// Types
// ───────────────────────────────────────────────────────────

export type AgreementKind = 'MOU' | 'NDA' | 'PARTNERSHIP' | 'SOW' | 'DPA' | 'OTHER';

export type AgreementStatus =
  | 'DRAFT' | 'IN_REVIEW' | 'SIGNED' | 'EXPIRED' | 'TERMINATED' | 'SUPERSEDED';

export type AgreementVersionOrigin =
  | 'LOPETTIA_EMITTED' | 'UPLOADED_COUNTERPARTY' | 'UPLOADED_SIGNED';

export type SignatureMethod =
  | 'DOCUSIGN' | 'PANDADOC' | 'WET_INK_SCANNED' | 'DIGITAL_LOPETTIA' | 'EXTERNAL';

export type SignerParty = 'LOPETTIA' | 'COUNTERPARTY';

export interface AgreementSummary {
  id: string;
  kind: AgreementKind;
  title: string;
  status: AgreementStatus;
  counterpartyDisplayName: string;
  accountId: string | null;
  accountName: string | null;
  opportunityId: string | null;
  signedDate: string | null;
  effectiveDate: string | null;
  expirationDate: string | null;
  ownerUserId: string | null;
  tags: string[];
  updatedAt: string | null;
}

export interface AgreementVersionSummary {
  id: string;
  version: number;
  origin: AgreementVersionOrigin;
  notes: string | null;
  createdAt: string | null;
  fieldValues: unknown;
}

export interface AgreementSignatureSummary {
  id: string;
  agreementVersionId: string;
  signerParty: SignerParty;
  signerName: string;
  signerTitle: string | null;
  signerEmail: string | null;
  method: SignatureMethod;
  signedAt: string;
}

export interface AgreementDetail extends AgreementSummary {
  tenantId: string;
  templateId: string | null;
  currentVersionId: string | null;
  governingLaw: string | null;
  governingJurisdiction: string | null;
  notes: string | null;
  createdAt: string | null;
  versions: AgreementVersionSummary[];
  signatures: AgreementSignatureSummary[];
}

export interface AgreementTemplateSummary {
  id: string;
  name: string;
  kind: AgreementKind;
  description: string | null;
  isSystem: boolean;
  currentVersionId: string | null;
  updatedAt: string | null;
}

export interface AgreementTemplateVersionDetail {
  id: string;
  version: number;
  bodyXhtml: string;
  schema: unknown;
  changelog: string | null;
  createdAt: string | null;
}

export interface AgreementTemplateDetail extends AgreementTemplateSummary {
  versions: AgreementTemplateVersionDetail[];
}

// ───────────────────────────────────────────────────────────
// API
// ───────────────────────────────────────────────────────────

export async function listAgreements(params?: {
  status?: AgreementStatus;
  kind?: AgreementKind;
  accountId?: string;
  opportunityId?: string;
}): Promise<AgreementSummary[]> {
  const { data } = await apiClient.get<AgreementSummary[]>('/agreements', { params });
  return data;
}

export async function getAgreement(id: string): Promise<AgreementDetail> {
  const { data } = await apiClient.get<AgreementDetail>(`/agreements/${id}`);
  return data;
}

export async function listAgreementTemplates(kind?: AgreementKind): Promise<AgreementTemplateSummary[]> {
  const { data } = await apiClient.get<AgreementTemplateSummary[]>('/agreements/templates', {
    params: kind ? { kind } : undefined,
  });
  return data;
}

export async function getAgreementTemplate(id: string): Promise<AgreementTemplateDetail> {
  const { data } = await apiClient.get<AgreementTemplateDetail>(`/agreements/templates/${id}`);
  return data;
}

export interface CreateAgreementRequest {
  kind: AgreementKind;
  title: string;
  counterpartyDisplayName: string;
  templateId?: string;
  accountId?: string;
  opportunityId?: string;
  ownerUserId?: string;
  tags?: string[];
  notes?: string;
  initialFieldValuesJson?: string;
}

export async function createAgreement(req: CreateAgreementRequest): Promise<AgreementSummary> {
  const { data } = await apiClient.post<AgreementSummary>('/agreements', req);
  return data;
}

export interface AddSignatureRequest {
  signerParty: SignerParty;
  signerName: string;
  signerTitle?: string;
  signerEmail?: string;
  method: SignatureMethod;
  signedAt: string;
}

export interface ImportSignedAgreementRequest {
  kind: AgreementKind;
  title: string;
  counterpartyDisplayName: string;
  signedDate: string;
  signatures: AddSignatureRequest[];
  templateId?: string;
  accountId?: string;
  opportunityId?: string;
  effectiveDate?: string;
  governingLaw?: string;
  governingJurisdiction?: string;
  ownerUserId?: string;
  tags?: string[];
  notes?: string;
  fieldValuesJson?: string;
  attachmentId?: string;
}

export async function importSignedAgreement(req: ImportSignedAgreementRequest): Promise<AgreementSummary> {
  const { data } = await apiClient.post<AgreementSummary>('/agreements/import-signed', req);
  return data;
}

export interface UpdateAgreementRequest {
  title?: string;
  fieldValuesJson?: string;
  status?: AgreementStatus;
  counterpartyDisplayName?: string;
  accountId?: string;
  opportunityId?: string;
  effectiveDate?: string;
  expirationDate?: string;
  signedDate?: string;
  governingLaw?: string;
  governingJurisdiction?: string;
  ownerUserId?: string;
  tags?: string[];
  notes?: string;
}

export async function updateAgreement(id: string, req: UpdateAgreementRequest): Promise<AgreementSummary> {
  const { data } = await apiClient.patch<AgreementSummary>(`/agreements/${id}`, req);
  return data;
}

// ───────────────────────────────────────────────────────────
// v3 — AI assist / PDF export / file upload / template editor
// ───────────────────────────────────────────────────────────

export interface RefineClauseRequest {
  clauseNumber: string;
  currentText: string;
  instruction: string;
}

export interface RefineClauseResponse {
  clauseNumber: string;
  refinedText: string;
  model: string;
  tokensIn: number;
  tokensOut: number;
  latencyMillis: number;
}

export async function refineClause(
  agreementId: string,
  req: RefineClauseRequest,
): Promise<RefineClauseResponse> {
  const { data } = await apiClient.post<RefineClauseResponse>(
    `/agreements/${agreementId}/clauses/ai-refine`,
    req,
  );
  return data;
}

/**
 * Download the rendered PDF of a specific version (or the current
 * version when versionId is omitted) as a Blob ready to save.
 */
export async function downloadAgreementPdf(
  agreementId: string,
  versionId?: string,
): Promise<Blob> {
  const url = versionId
    ? `/agreements/${agreementId}/versions/${versionId}/export.pdf`
    : `/agreements/${agreementId}/export.pdf`;
  const { data } = await apiClient.get<Blob>(url, { responseType: 'blob' });
  return data;
}

export async function uploadCounterpartyVersion(
  agreementId: string,
  file: File,
  notes?: string,
): Promise<AgreementVersionSummary> {
  const fd = new FormData();
  fd.append('file', file);
  if (notes) fd.append('notes', notes);
  const { data } = await apiClient.post<AgreementVersionSummary>(
    `/agreements/${agreementId}/versions/upload`,
    fd,
    { headers: { 'Content-Type': 'multipart/form-data' } },
  );
  return data;
}

// ─── Template admin ─────────────────────────────────────────

export interface CreateAgreementTemplateRequest {
  name: string;
  kind: AgreementKind;
  description?: string;
  bodyXhtml?: string;
  schemaJson?: string;
}

export interface UpdateAgreementTemplateRequest {
  name?: string;
  description?: string;
}

export interface AddTemplateVersionRequest {
  bodyXhtml: string;
  schemaJson?: string;
  changelog?: string;
}

export async function createAgreementTemplate(
  req: CreateAgreementTemplateRequest,
): Promise<AgreementTemplateSummary> {
  const { data } = await apiClient.post<AgreementTemplateSummary>(
    '/agreements/templates',
    req,
  );
  return data;
}

export async function updateAgreementTemplate(
  id: string,
  req: UpdateAgreementTemplateRequest,
): Promise<AgreementTemplateSummary> {
  const { data } = await apiClient.patch<AgreementTemplateSummary>(
    `/agreements/templates/${id}`,
    req,
  );
  return data;
}

export async function addAgreementTemplateVersion(
  id: string,
  req: AddTemplateVersionRequest,
): Promise<AgreementTemplateVersionDetail> {
  const { data } = await apiClient.post<AgreementTemplateVersionDetail>(
    `/agreements/templates/${id}/versions`,
    req,
  );
  return data;
}

export async function cloneAgreementTemplate(
  id: string,
  newName?: string,
): Promise<AgreementTemplateSummary> {
  const { data } = await apiClient.post<AgreementTemplateSummary>(
    `/agreements/templates/${id}/clone`,
    null,
    { params: newName ? { newName } : undefined },
  );
  return data;
}
